// Hero slide equalizer — make every slide the height of the tallest, from the start.
//
// Why it's needed: the hero mixes slide TYPES. A full-bleed background slide (slide 1) is sized to the
// viewport, while "split" slides stack a content column + a media band and, on a short phone, that
// group can grow PAST the viewport. The full-bleed slide doesn't get pulled up to match (a flex /
// height:100% subtlety), so slide 1 ends up SHORTER than slides 2+. The slideshow's own
// align-items:stretch is supposed to equalize them but doesn't here.
//
// What it does: measure each slide's natural height, take the tallest, and set that as a `min-height`
// on EVERY slide. Crucially min-height, not a fixed height — it can only GROW the short slide up to
// match, never clip a tall one (a fixed height + overflow:hidden clipped content in an earlier
// attempt). We also force the lazy slide images to load up front so the tallest is known before the
// visitor swipes (otherwise an image loading on focus would grow its slide mid-swipe).
//
// Re-measured on resize, fonts ready, and once all images have settled. No-JS fallback: the CSS min-height + stretch still roughly equalizes.
//
// FIRST measure does NOT happen here. This file is loaded as type="module", which is deferred by spec,
// so its earliest possible run is after the document is parsed — measured 2026-07-31 at 618ms against a
// 600ms FCP, i.e. one painted frame too late, which showed up as 0.0545 of 0.0586 desktop CLS when the
// min-height grew the slides after paint. sections/hero-slider.liquid therefore runs an INLINE copy of
// `measure()` below, synchronously during parse, so the first painted frame is already equalized.
// This module then owns every re-measure. KEEP THE TWO IN SYNC — if you change the algorithm in
// `measure()`, change the inline copy in hero-slider.liquid too.

import { requestIdleCallback as onIdle } from '@theme/utilities';

(() => {
  const SELECTOR = 'slideshow-component.hero-slider';

  // Measure natural slide heights and force them all to the tallest via min-height.
  const measure = (component) => {
    // Skip slides hidden for this viewport (hero-slide-visibility.js sets [hidden]) — a desktop-only
    // slide must not contribute to the mobile equalized height, or vice versa.
    const slides = Array.from(component.querySelectorAll('slideshow-slide:not([hidden])'));
    if (slides.length < 2) return;

    // Force off-screen slides to render for THIS synchronous measure only. They're
    // content-visibility:auto otherwise (the base slideshow style), which reports ~0 height; the
    // `.hero-equalizing` class flips them visible via the section CSS just long enough to read real
    // heights. The min-height we pin below then holds each slide's height even after render-skip
    // resumes, so the equalization survives without paying the permanent render cost. All of this is
    // one synchronous task (class add -> read -> class remove), so no intermediate frame is painted.
    component.classList.add('hero-equalizing');

    // Clear our own floor + un-stretch + uncap so each slide reports its OWN natural height.
    slides.forEach((slide) => {
      slide.style.minHeight = '';
      slide.style.alignSelf = 'flex-start';
      slide.style.maxHeight = 'none';
    });

    let max = 0;
    slides.forEach((slide) => {
      max = Math.max(max, slide.getBoundingClientRect().height);
    });

    // Restore, then floor every slide at the tallest. min-height only grows the short ones up to
    // match — it can never clip a slide whose content is taller, so nothing is ever cut off.
    slides.forEach((slide) => {
      slide.style.alignSelf = '';
      slide.style.maxHeight = '';
      if (max > 0) slide.style.minHeight = `${Math.round(max)}px`;
    });

    component.classList.remove('hero-equalizing');
  };

  class HeroEqualizer {
    constructor(component) {
      this.component = component;
      this.raf = 0;
      this.idle = 0;
      this.settleId = 0;
      this.schedule = this.schedule.bind(this);
      this.settle = this.settle.bind(this);
      this.run = this.run.bind(this);

      // The section's inline pre-paint script (sections/hero-slider.liquid) runs this exact pass
      // during parse, so repeating it here would redo identical layout work for an identical result
      // — a wasted forced reflow on every load. Consume the flag rather than just reading it, so a
      // later re-init (shopify:section:load in the editor) measures normally.
      if (component.__heroPrepaintEqualized) component.__heroPrepaintEqualized = false;
      else this.run();

      // Re-measure on what changes a slide's natural height. We use window `resize` (not a
      // ResizeObserver on the track): slideshow.js already observes the container, and our own
      // min-height writes change it — observing it ourselves would risk a measure→resize→measure loop.
      window.addEventListener('resize', this.schedule, { passive: true });
      window.addEventListener('load', this.settle, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.run).catch(() => {});

      // Force every off-screen slide image to load, then re-measure once they've all settled.
      // Off-screen slides are loading="lazy", so an image would otherwise load the instant you swipe
      // to its slide, growing it under you. Loading up front means the tallest is known before any
      // interaction — BUT we deliberately DEFER this pass until after `load` and one idle tick. The
      // first-slide LCP image is eager/high and must get an uncontested head start; kicking off N
      // low-priority slide downloads in parallel with it can lengthen LCP on slow connections (CDNs
      // don't always honour fetchPriority arbitration). Deferring costs nothing perceptible — the
      // visitor can't swipe to slide 2+ in the first idle moment anyway.
      this.deferForceLoad();
    }

    // Eager-load the off-screen slide images once the browser is idle post-`load`, so the LCP hero
    // image never competes with them for bandwidth.
    deferForceLoad() {
      const forceLoad = () => {
        if (!document.contains(this.component)) return;
        const skipHover = window.matchMedia('(hover: none)').matches ? ':not(.product-card-editorial__hover-media)' : '';
        const images = Array.from(
          this.component.querySelectorAll(`slideshow-slide img:not(.hero-slide__gallery-img--deferred)${skipHover}`)
        );
        images.forEach((img) => {
          if (img.loading === 'lazy') {
            img.loading = 'eager';
            img.fetchPriority = 'low';
          }
        });
        const pending = images.filter((img) => !img.complete);
        let remaining = pending.length;
        if (remaining > 0) {
          const settle = () => {
            remaining -= 1;
            if (remaining === 0) this.settle();
          };
          pending.forEach((img) => {
            img.addEventListener('load', settle, { once: true });
            img.addEventListener('error', settle, { once: true });
          });
        }
      };

      const scheduleForceLoad = () => {
        if (typeof requestIdleCallback === 'function') {
          this.idle = requestIdleCallback(forceLoad, { timeout: 1500 });
        } else {
          this.idle = setTimeout(forceLoad, 200);
        }
      };

      if (document.readyState === 'complete') scheduleForceLoad();
      else window.addEventListener('load', scheduleForceLoad, { once: true, passive: true });
    }

    schedule() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.run();
      });
    }

    settle() {
      if (this.settleId) return;
      this.settleId = onIdle(
        () => {
          this.settleId = 0;
          this.run();
        },
        { timeout: 250 }
      );
    }

    run() {
      if (!document.contains(this.component)) return;
      measure(this.component);
    }

    destroy() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.idle) {
        if (typeof cancelIdleCallback === 'function') cancelIdleCallback(this.idle);
        else clearTimeout(this.idle);
      }
      window.removeEventListener('resize', this.schedule);
      if (this.settleId) {
        if (typeof cancelIdleCallback === 'function') cancelIdleCallback(this.settleId);
        else clearTimeout(this.settleId);
      }
      window.removeEventListener('load', this.settle);
      this.component.querySelectorAll('slideshow-slide').forEach((slide) => {
        slide.style.minHeight = '';
      });
    }
  }

  const instances = new Set();

  const init = (component) => {
    if (component.__heroEqualizer) return;
    component.__heroEqualizer = new HeroEqualizer(component);
    instances.add(component.__heroEqualizer);
  };

  const reap = () => {
    instances.forEach((inst) => {
      if (!document.contains(inst.component)) {
        inst.destroy();
        inst.component.__heroEqualizer = null;
        instances.delete(inst);
      }
    });
  };

  const boot = () => document.querySelectorAll(SELECTOR).forEach(init);

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', (event) => {
    reap();
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root.matches && root.matches(SELECTOR)) init(root);
    root.querySelectorAll(SELECTOR).forEach(init);
  });
  document.addEventListener('shopify:section:unload', reap);
})();
